/**
 * DispatcherPortalApi — the DISPATCHER half of the portal's HTTP calls.
 *
 * Built on the portal's own client (`portalClient` + `payloadOf` from
 * PortalApi.js): the portal token, silent refresh and the "never send the
 * admin token" guarantee all come from there. Nothing here sends an
 * operator id, a driver id or a login kind — the API takes the principal
 * from the token and the board is PH-wide by definition.
 *
 * The board reads; `/portal/dispatcher/assign` is the one thing it writes.
 *
 * Response shapes (see ph-core portalDispatcherViews.helper.ts):
 *
 *   GET /portal/dispatcher/board?from&to&page
 *     → { from, to, live, generated_at, total (BOOKINGS, not rows),
 *         page, page_size, has_more, capped,
 *         summary: { trips, drivers_working, unassigned, needs_attention,
 *                    attention: { late, waiting, no_show } },
 *         trips: DispatcherTrip[] }
 *   `trips` is ONE PAGE of the period, in pickup order; `has_more` says
 *   whether to ask for the next. `summary` counts the WHOLE period on every
 *   page, so the numbers above the list never describe only what has been
 *   scrolled past. `capped` is the one thing paging cannot fix: a period
 *   holding more bookings than the board will serve at all.
 *   DispatcherTrip = {
 *     _id, booking_reference, status, booking_type, date_datetime,
 *     from: { address, latitude?, longitude? }, to: { … }, category,
 *     passengers: { adults, children?, booster_seat?, convertible_seat?, infant_carrier? },
 *     customer_name, customer_phone?, customer_phone_visible_from?,
 *     flight_number?, operator_comments?, programme?, event_name?,
 *     ph_branding_allowed, passenger_manifest,
 *     operator?: { id, name? }, role?, driver?: { id, full_name? },
 *     vehicle?: { type?, plate_number?, color? }, lead, needs_driver,
 *     attention?: 'late' | 'waiting' | 'no_show', overtime_running_since?,
 *     driver_status?: { state, date }, driver_overtime, overtime_total_minutes }
 *   A Confirmed booking with no operator at all is one `needs_driver` row
 *   with neither `operator` nor `driver`.
 *
 *   GET /portal/dispatcher/expenses?from&to
 *     → { from, to, expenses: [{ _id, type, amount, currency, note?, status,
 *         created_at, booking_id?, booking_reference?,
 *         operator: { id, name? }, submitted_by: { kind, name? } }] }
 *
 *   GET  /portal/dispatcher/trip?booking_id&slot_operator_id&role
 *     → { booking_id,
 *         slot: { operator_id?, role, lead },
 *         partner: { direct, name?, phone_number?, email? },
 *         fee: { operator_price?, operator_currency?, total?,
 *                adjustments: [{ kind, description?, amount, currency }] } }
 *   The two things ONE trip carries that a board ROW does not: who sold it,
 *   and what we pay the operator on that slot with each adjustment named.
 *   `partner.direct` is Pickup Hungary's own booking — there is nobody to
 *   chase. The slot is named the way a row describes itself; omitted, it is
 *   the trip's first. It returns no trip: the screen has the row already.
 *
 *   GET  /portal/dispatcher/assign?booking_id&slot_operator_id&role&add
 *     → { booking_id,
 *         slot: { operator_id?, role, lead, add?, driver_id?, car_id?,
 *                 operator_price?, operator_currency? },
 *         operators: [{ id, name? }],
 *         drivers:  [{ id, operator_id, full_name? }],
 *         vehicles: [{ id, operator_id, type?, plate_number?, color? }] }
 *   POST /portal/dispatcher/assign
 *     { booking_id, operator_id, slot_operator_id?, role?, add?, driver_id?,
 *       car_id?, operator_price?, operator_currency? }
 *     → { trip: DispatcherTrip, operator_price?, operator_currency? }
 *   `add: true` puts a NEW slot on the trip — a guide beside the driver —
 *   rather than changing one it already has. It names its own `role` (which
 *   is then required) and its own fee, and is appended, so the lead's driver
 *   and car are never touched by it.
 *
 * MONEY. The board and the claims panel carry none — a list of a month of
 * trips is not where the office reads prices. Exactly one figure travels,
 * and only where a dispatcher is dealing with ONE slot of ONE trip:
 * `operator_price` / `operator_currency`, what WE PAY THE OPERATOR on it.
 * It is on the assign dialog (a trip cannot be handed over without saying
 * what it is worth) and on the trip screen with its adjustments named (a fee
 * 30 over what was agreed is a telephone call, and the office should not
 * have to open a laptop to make it). It is on no board row and in no claims
 * panel. The CUSTOMER's price, the profit and the order lines as a ledger
 * are on none of these shapes at all.
 *
 * THE PARTNER, likewise, is on the trip shape and nowhere else — and is the
 * partner's NAME, number and address, never `partner_id` and never our own
 * `partner_booking_reference`.
 */
/* eslint-disable no-underscore-dangle */
import { payloadOf, portalClient } from './PortalApi';
import compact from '../Utils/compactParams';

export const fetchDispatcherBoard = async ({ from, to, page = 1 } = {}) => {
  const payload = payloadOf(await portalClient.get('/portal/dispatcher/board', {
    params: compact({ from, to, page }),
  }));

  return {
    ...payload,
    trips: Array.isArray(payload?.trips) ? payload.trips : [],
    summary: payload?.summary || {
      trips: 0,
      drivers_working: 0,
      unassigned: 0,
      needs_attention: 0,
      attention: { late: 0, waiting: 0, no_show: 0 },
    },
  };
};

export const fetchDispatcherExpenses = async ({ from, to } = {}) => {
  const payload = payloadOf(await portalClient.get('/portal/dispatcher/expenses', {
    params: compact({ from, to }),
  }));

  return Array.isArray(payload?.expenses) ? payload.expenses : [];
};

/* --- the board's one write ---------------------------------------------- */

/** The slot a board row stands for, as the API names it. */
const slotOf = (trip) => compact({ slot_operator_id: trip?.operator?.id, role: trip?.role });

/**
 * What the assign dialog may choose from.
 *
 * `add` asks for a slot the trip has NOT got yet, so the dialog is handed an
 * empty one — no operator, no crew, no fee — instead of the slot the row it
 * was opened from stands for. The role is chosen in the sheet, so it is not
 * sent here.
 */
export const fetchDispatcherAssignOptions = async (trip, { add = false } = {}) => payloadOf(
  await portalClient.get('/portal/dispatcher/assign', {
    params: compact({
      booking_id: trip?._id,
      // An addition names no existing slot: sending the row's would ask the
      // API to add what is already there, which it refuses.
      ...(add ? { add: '1' } : slotOf(trip)),
    }),
  }),
);

export const assignDispatcherTrip = async ({
  trip, operatorId, driverId, carId, fee, add = false, role,
}) => payloadOf(await portalClient.post('/portal/dispatcher/assign', compact({
  booking_id: trip?._id,
  ...(add ? { add: true, role } : slotOf(trip)),
  operator_id: operatorId,
  driver_id: driverId,
  car_id: carId,
  operator_price: fee?.amount,
  operator_currency: fee?.amount === undefined ? undefined : fee?.currency,
})));

/* --- one trip: who sold it, and what this slot pays ---------------------- */

/**
 * The partner and the slot's fee for ONE trip.
 *
 * Asked for alongside the board read the trip screen already makes, never
 * instead of it: the board is what knows who is driving and what step they
 * are on, and this is the two things it deliberately does not carry.
 *
 * Both halves are defaulted, so a screen never has to guard them: a trip
 * whose partner cannot be read shows as Pickup Hungary's own rather than as
 * a broken card, and a slot with no fee agreed shows no fee rather than a
 * zero.
 */
export const fetchDispatcherTrip = async (trip) => {
  const payload = payloadOf(await portalClient.get('/portal/dispatcher/trip', {
    params: compact({ booking_id: trip?._id, ...slotOf(trip) }),
  }));

  return {
    ...payload,
    partner: payload?.partner || { direct: true },
    fee: {
      ...payload?.fee,
      adjustments: Array.isArray(payload?.fee?.adjustments) ? payload.fee.adjustments : [],
    },
  };
};
